import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { LoadingSpinner } from "../../components/LoadingSpinner";
import { ToastContext } from "../../context/ToastContext";
import {
  useGetFileQuery,
  useUpdateFileMutation,
} from "../../store/queries/chat";
import { IUpdateFileRequest } from "../../types/Chat";

const FormContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
`;

const FormField = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const WarningText = styled.div`
  margin-top: 8px;
  padding: 12px;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  font-size: 14px;
  color: #856404;
`;

const ReadOnlyText = styled.div`
  margin-top: 8px;
  padding: 12px;
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  font-size: 14px;
  color: #6c757d;
`;

export function EditFilePage() {
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const { current: toastElement } = useContext(ToastContext);
  const { data: fileData, isLoading: isLoadingFile } = useGetFileQuery(fileId!);
  const [updateFile, { isLoading: isUpdating }] = useUpdateFileMutation();

  const [formData, setFormData] = useState({
    name: "",
    content: "",
  });

  useEffect(() => {
    if (fileData?.db) {
      setFormData({
        name: fileData.db.name,
        content: fileData.db.content || "",
      });
    }
  }, [fileData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!fileId) return;

    try {
      const submitData: IUpdateFileRequest = {
        name: formData.name,
        content: formData.content,
      };

      await updateFile({ fileId, data: submitData }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Success!",
        detail: "File updated successfully.",
      });

      navigate(`/chatbot/files/${fileId}`);
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to update file.",
      });
    }
  };

  const handleCancel = () => {
    navigate(`/chatbot/files/${fileId}`);
  };

  if (isLoadingFile) {
    return <LoadingSpinner />;
  }

  if (!fileData?.db) {
    return (
      <FormContainer>
        <Card title="File Not Found">
          <p>The requested file could not be found.</p>
          <Button
            label="Back to Files"
            onClick={() => navigate("/chatbot/files")}
          />
        </Card>
      </FormContainer>
    );
  }

  const { db: dbFile } = fileData;
  const isGuideFile =
    dbFile.identifier === "EligibilityProcessingGuide" ||
    dbFile.identifier === "PricingAdjustmentsGuide" ||
    dbFile.identifier === "ContractManagementGuide";

  if (isGuideFile) {
    return (
      <FormContainer>
        <Card title="Cannot Edit Guide File">
          <Message
            severity="warn"
            text="Guide files are managed as source code within our application codebase and cannot be edited through this interface. These files must be updated in the codebase to ensure they stay synchronized with business logic changes."
          />
          <ButtonContainer>
            <Button
              label="Back to File Details"
              className="p-button-outlined"
              onClick={handleCancel}
            />
          </ButtonContainer>
        </Card>
      </FormContainer>
    );
  }

  return (
    <FormContainer>
      <Card title="Edit File">
        <form onSubmit={handleSubmit}>
          <FormField>
            <Label>File Identifier</Label>
            <ReadOnlyText>{dbFile.identifier}</ReadOnlyText>
          </FormField>

          <FormField>
            <Label htmlFor="name">File Name *</Label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              className="w-full"
              placeholder="Enter file name"
            />
          </FormField>

          <FormField>
            <Label htmlFor="content">File Content *</Label>
            <InputTextarea
              id="content"
              value={formData.content}
              onChange={(e) =>
                setFormData({ ...formData, content: e.target.value })
              }
              required
              rows={15}
              className="w-full"
              placeholder="Enter file content"
            />
            <WarningText>
              <strong>Important:</strong> Updating this file will create a new
              version on OpenAI&apos;s servers and automatically update all chat
              models that reference this file. Vector stores will be recreated
              to reflect the changes.
            </WarningText>
          </FormField>

          <ButtonContainer>
            <Button
              type="button"
              label="Cancel"
              className="p-button-outlined"
              onClick={handleCancel}
            />
            <Button
              type="submit"
              label="Update File"
              className="p-button-raised"
              loading={isUpdating}
              disabled={!formData.name.trim() || !formData.content.trim()}
            />
          </ButtonContainer>
        </form>
      </Card>
    </FormContainer>
  );
}
